// Client auth wiring (plan D10, revised by docs/plan/08-auth-worker.md). The
// SPA stores NOTHING: the session JWT lives in an httpOnly cookie set by the
// Cloudflare auth worker, and page JS only ever holds a copy in memory, fetched
// on demand for Convex's `setAuth`. The `?code=` exchange, cookie, and JWT
// verification all live in the worker (phase 8) — this module just redirects
// into the broker and reads the token endpoint.

import { useSyncExternalStore } from "react";
import type { ConvexReactClient } from "convex/react";
import type { SessionPayload } from "@bindery/convex/model/session";
import { STATE_BYTES } from "@worker/config";
import { buildStateCookie } from "@worker/cookies";

/** Where sign-in leaves for. Exported because a RETURN hop from it is one of
 *  our own redirects, not an acquisition referral (`logging/attribution.ts`). */
export const AUTH_BROKER_ORIGIN = "https://auth.sara.ai";

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

/**
 * A fresh sign-in nonce: 16 CSPRNG bytes as base64url. base64url rather than
 * base64 because the broker rebuilds the callback's query through
 * `URLSearchParams`, which would turn a literal `+` into a space.
 */
function mintState(): string {
  const bytes = new Uint8Array(STATE_BYTES);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Begin sign-in: mint a nonce, redirect (top-level) into the broker with THIS
 * origin's worker `/auth/callback` as the return target; the broker appends a
 * one-time `?code=` the worker then exchanges.
 *
 * The nonce is the login-CSRF defence. It goes into a short-TTL, host-only
 * cookie AND onto the callback URL as `state`; the broker preserves the
 * callback's existing query params, so both halves come back together and
 * `/auth/callback` refuses any code whose `state` doesn't match the cookie.
 * Without it, an attacker could read a code off their own sign-in and get a
 * victim's browser to load our callback with it — `SameSite=Lax` governs
 * cookie SENDING, not SETTING, so even an `<img>` would have landed the
 * `Set-Cookie` and silently signed the victim in as the attacker.
 *
 * The cookie is written here rather than by the worker because the worker never
 * sees this hop — sign-in leaves straight for the broker. It is scoped to
 * `/auth` like the worker's own cookies, so it never rides an asset request,
 * which also means page JS cannot READ it back: each `signIn` overwrites rather
 * than appends. Two tabs mid-sign-in therefore share one pending nonce, and
 * whichever finishes second gets `?auth_error=state_mismatch` and signs in on
 * the next click. That is a deliberate trade against holding a readable
 * multi-value nonce cookie on `/`.
 *
 * `retells.sara.ai` and `retells.read-by-ear.com` are peer mirrors, so sign-in
 * stays on the host the user is already on — each serves `/auth/*` and holds
 * its own host-only session cookie (`src/worker/config.ts` `AUTH_HOSTS`). This
 * used to bounce read-by-ear to a single canonical host first, which dead-ended
 * whenever that host wasn't the one actually deployed.
 */
export function signIn(returnTo: string = currentPath()): void {
  const state = mintState();
  document.cookie = buildStateCookie(
    state,
    window.location.protocol === "https:",
  );
  const callback =
    `${window.location.origin}/auth/callback` +
    `?returnTo=${encodeURIComponent(returnTo)}&state=${state}`;
  window.location.assign(
    `${AUTH_BROKER_ORIGIN}/redirect?returnTo=${encodeURIComponent(callback)}`,
  );
}

/**
 * Read and strip a `?auth_error=` query param left by the worker's failed
 * `/auth/callback` exchange (docs/plan/08-auth-worker.md), or `null` if none
 * is present. Previously surfaced nowhere, so a failed exchange was silent
 * (review MINOR finding 6). Call once at boot and surface the result (e.g. a
 * toast); the param must not linger in the URL bar or survive a refresh.
 */
export function consumeAuthErrorParam(): string | null {
  const url = new URL(window.location.href);
  const authError = url.searchParams.get("auth_error");
  if (authError === null) return null;
  url.searchParams.delete("auth_error");
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  return authError;
}

/**
 * Sign out via the worker `/auth/sign-out`, which clears the session cookie
 * and bounces through the broker sign-out so the WorkOS session dies too
 * (otherwise the next silent `autoSignIn` would sign the user straight back
 * in).
 */
export function signOut(returnTo: string = currentPath()): void {
  window.location.assign(
    `/auth/sign-out?returnTo=${encodeURIComponent(returnTo)}`,
  );
}

/**
 * Convex `setAuth` token source. GETs the worker `/auth/token`, which serves
 * the cookie's JWT in-memory only. No cookie / expired → 401 → `null` →
 * Convex stays anonymous (the whole reading experience works unauthenticated).
 * `forceRefreshToken` needs no special handling: the worker re-serves the same
 * cookie, and a truly expired cookie yields `null`.
 */
export async function fetchToken(): Promise<string | null> {
  try {
    const res = await fetch("/auth/token", { credentials: "same-origin" });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "token" in body &&
      typeof body.token === "string"
    ) {
      return (body as { token: string }).token;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Point a Convex client at the cookie-backed token source (call once at
 * boot). `onChange`, when supplied, is `setAuth`'s second argument — Convex
 * calls it with the resolved `isAuthenticated` state once the token fetch
 * settles; it's the designated trigger for a fresh sign-in's
 * `bookmarks.mergeLocal` (plan 08 §4, wired up by phase 5).
 *
 * The same callback also feeds the module store behind `useIsAuthenticated`.
 */
export function wireConvexAuth(
  client: ConvexReactClient,
  onChange?: (isAuthenticated: boolean) => void,
): void {
  client.setAuth(fetchToken, (isAuthenticated) => {
    publishAuthenticated(isAuthenticated);
    onChange?.(isAuthenticated);
  });
}

// --- Signed-in state for the UI (plan 08 §4's "UI state" callback) ---------
// We mount a plain `ConvexProvider` (src/main.tsx), so `convex/react`'s
// `useConvexAuth` is unusable — it reads a context only `ConvexProviderWithAuth`
// installs and throws "Could not find `ConvexProviderWithAuth` … as an ancestor"
// for anything that calls it. `setAuth`'s onChange is the sanctioned substitute:
// Convex fires `true` once the server confirms a token and `false` whenever the
// fetch yields none or the token is rejected. Mirror it into a tiny store so
// components can subscribe without a provider.

/**
 * `"unknown"` until Convex's `setAuth` onChange has fired even once. It is a
 * real third state, not a placeholder for `false`: chrome that renders on
 * first paint has to be able to say "I don't know yet" rather than assert
 * "signed out", or a signed-in reader is told to sign in for as long as the
 * token fetch and the WebSocket handshake take.
 */
export type AuthState = "unknown" | "authenticated" | "anonymous";

let authState: AuthState = "unknown";
const authListeners = new Set<() => void>();

function subscribeAuthenticated(onStoreChange: () => void): () => void {
  authListeners.add(onStoreChange);
  return () => {
    authListeners.delete(onStoreChange);
  };
}

function publishAuthenticated(next: boolean): void {
  const resolved: AuthState = next ? "authenticated" : "anonymous";
  if (resolved === authState) return;
  authState = resolved;
  for (const listener of authListeners) listener();
}

/**
 * Convex's view of the session, INCLUDING whether it has one yet. Starts
 * `"unknown"` and resolves as `setAuth`'s onChange fires.
 *
 * Use this — not `useIsAuthenticated` — anywhere the signed-out branch is
 * something a reader can SEE, because the two branches are not each other's
 * negation until the state settles. `AppNav` is the case that forced this: it
 * renders in the corner of every route on first paint, so collapsing
 * `"unknown"` into "anonymous" put a "Sign in" button in front of readers who
 * were already signed in, for the length of the boot token fetch.
 */
export function useAuthState(): AuthState {
  return useSyncExternalStore(subscribeAuthenticated, () => authState);
}

/**
 * Whether Convex holds a CONFIRMED session — `false` covers both "no session"
 * and "not resolved yet". Fine for gating a query argument or a control that
 * only exists behind an interaction (by which time the state has settled);
 * see `useAuthState` for anything visible on first paint.
 */
export function useIsAuthenticated(): boolean {
  return useAuthState() === "authenticated";
}

const INTERNAL_ROLES = ["founder", "admin", "support", "researcher"] as const;
function isInternalRole(v: unknown): v is SessionPayload["internalRole"] {
  return (
    typeof v === "string" && (INTERNAL_ROLES as readonly string[]).includes(v)
  );
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Decode the broker JWT's payload leniently into the consumed subset
 * (`convex/model/session.ts`). This is a real base64/JSON boundary — a
 * malformed token must yield `null`, never throw — and it is NOT verification
 * (Convex verifies the signature); it only reads display/correlation claims
 * (e.g. `readerId` for logging). Extra claims are tolerated and dropped.
 */
export function decodeSessionPayload(jwt: string): SessionPayload | null {
  const parts = jwt.split(".");
  const [, payloadPart] = parts;
  if (parts.length !== 3 || payloadPart === undefined) return null;

  let raw: unknown;
  try {
    raw = JSON.parse(decodeBase64Url(payloadPart));
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null) return null;

  const claims = raw as Record<string, unknown>;
  if (claims.typ !== "user_session") return null;
  if (typeof claims.workosId !== "string") return null;

  const payload: SessionPayload = {
    typ: "user_session",
    workosId: claims.workosId,
  };
  const copyString = (key: keyof SessionPayload) => {
    const value = claims[key];
    if (typeof value === "string") {
      (payload as Record<string, unknown>)[key] = value;
    }
  };
  copyString("sub");
  copyString("email");
  copyString("firstName");
  copyString("lastName");
  copyString("profilePictureUrl");
  copyString("readerId");
  copyString("readerNickname");
  if (isInternalRole(claims.internalRole)) {
    payload.internalRole = claims.internalRole;
  }
  return payload;
}
